import React, { useEffect, useState } from 'react';
import CustomModal from 'components/CustomModal/CustomModal';
import { IconReopen, IconClose } from 'components/Icons';
import { KSButton } from 'presentation/components/KSQ2/Common';
import './ActionDialog.scss';

interface ReopenDialogProps {
  isOpen: boolean;
  isPending: boolean;
  onClose: () => void;
  onSubmit: (form: { comment: string }) => void;
  title?: string;
  commentLabel?: string;
  commentPlaceholder?: string;
  submitLabel?: string;
  infoMessage?: string;
}

/**
 * Modal dialog for reopening a request
 * Allows adding an optional reopening comment
 */
export const ReopenDialog: React.FC<ReopenDialogProps> = ({
  isOpen,
  isPending,
  onClose,
  onSubmit,
  title = 'Reopen Request',
  commentLabel = 'Reopen comment (optional)',
  commentPlaceholder = 'Add a note explaining why this request is being reopened.',
  submitLabel = 'Reopen',
  infoMessage,
}) => {
  const [comment, setComment] = useState<string>('');

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setComment('');
      }, 300);
    }
  }, [isOpen]);

  const handleSubmit = () => {
    onSubmit({ comment });
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="resolve-modal"
      shouldCloseOnEsc={!isPending}
      shouldCloseOnOverlayClick={!isPending}
    >
      <div className="resolve-modal__content">
        <h2 className="resolve-modal__title">{title}</h2>
        {infoMessage && <p className="resolve-modal__info">{infoMessage}</p>}

        <div className="resolve-modal__form">
          <div className="resolve-modal__field">
            <label className="resolve-modal__label">{commentLabel}</label>
            <textarea
              className="resolve-modal__textarea"
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder={commentPlaceholder}
              disabled={isPending}
              rows={4}
            />
          </div>
        </div>

        <div className="resolve-modal__actions">
          <KSButton variant="secondary" icon={<IconClose />} label="Cancel" onClick={onClose} disabled={isPending} />
          <KSButton
            variant="primary"
            icon={isPending ? <i className="fa fa-circle-o-notch fa-spin" /> : <IconReopen />}
            label={isPending ? 'Reopening...' : submitLabel}
            onClick={handleSubmit}
            disabled={isPending}
          />
        </div>
      </div>
    </CustomModal>
  );
};

export default ReopenDialog;
